import React, { useCallback, useMemo, useRef, useState, MouseEvent } from 'react';
import styles from '../multi-select.module.scss';
import { SelectOption } from '../../../types';
import ChevronIcon from '../../../assets/icons/chevron_down.svg?react';
import { useOnClickOutside } from '../../../hooks/useClickOutside';
import IconEmpty from '../../../assets/icons/checkbox.svg?react';
import IconFill from '../../../assets/icons/checkbox_fill.svg?react';
import { TextInput } from '../../text-input/TextInput';

type Props = {
  label: string;
  renderLabel?: (labelComponent: React.ReactNode) => React.ReactNode;
  options: SelectOption[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  value: string[];
  className?: string;
  inline?: boolean;
  subTitle?: string;
  error?: boolean;
  showSearch?: boolean;
  searchAutoFocus?: boolean;
  inDropDown?: boolean;
};

function SelectMulti(props: Props) {
  const {
    className,
    label,
    placeholder = 'All',
    options = [],
    value,
    onChange,
    disabled,
    renderLabel,
    inline,
    subTitle,
    error,
    showSearch = true,
    searchAutoFocus,
    inDropDown
  } = props;
  const [opened, setOpened] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');

  useOnClickOutside(wrapperRef, () => {
    setOpened(false);
  });

  const wrapperClasses = useMemo(() => {
    const classes = [styles.wrapper, className];
    if (inline) {
      classes.push(styles.inline);
    }
    if (opened) {
      classes.push(styles.opened);
    }
    if (error) {
      classes.push(styles.error);
    }
    if (disabled) {
      classes.push(styles.disabled);
    }
    return classes.join(' ').trim();
  }, [opened, className, disabled, inline, error]);

  const handleToggle = () => {
    if (disabled) return;
    setOpened(!opened);
  };
  const handleChange = useCallback((option: SelectOption) => (e: MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();

    const index = value.findIndex(v => v === option.value);
    if (index === -1) {
      onChange([
        ...value,
        option.value,
      ]);
    } else {
      onChange(value.filter(v => v !== option.value));
    }
  }, [onChange, value]);

  const selectedValues = useMemo(() => {
    if (!value) return [];
    const items: SelectOption[] = [];
    value.forEach(v => {
      const o = options.find(o => o.value === v);
      if (o) {
        items.push(o);
      }
    });
    return items.map(o => o.label);
  }, [options, value]);

  const handleChangeSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleStop = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const sorted = useMemo(() => {
    if (!options) return [];
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }, [options]);

  const filtered = useMemo(() => {
    if (!search) return sorted;
    const s = search.toLowerCase();
    return sorted.filter(o => o.label.toLowerCase().includes(s));
  }, [sorted, search]);

  return (
    <div className={wrapperClasses} ref={wrapperRef} onClick={handleToggle}>
      <div className={styles.header}>
        {typeof renderLabel === 'function' ? renderLabel(<div className={styles.headerLabel}>{label}</div>) :
          <div className={styles.headerLabel}>{label}</div>}
      </div>
      <div className={styles.selectWrapper}>
        <div className={styles.select}>
          {selectedValues.length ? <div className={styles.selectedTitle}>{selectedValues.join(', ')}</div> :
            <div className={styles.placeholder}>
              {placeholder}
            </div>}
          <div className={styles.chevronWrapper}>
            <ChevronIcon className={styles.chevron}/>
          </div>
        </div>
        {subTitle && <div className={styles.subtitle}>{subTitle}</div>}
        {opened &&
        <ul className={styles.list}>
          {showSearch && <li className={styles.search} onClick={handleStop}>
            <TextInput allowSpecialCharacters autoFocus={searchAutoFocus} placeholder={'Search'} label={''} id={'search'} value={search} onChange={handleChangeSearch}  inDropDown={inDropDown}/>
          </li>}
          {filtered.length ?
            filtered.map(o => {
              const selected = (value as any[]).find((c) => o.value === c);
              return (
                <li key={o.value} onClick={handleChange(o)} className={styles.listItem}>
                  <div className={styles.checkboxWrapper}>
                    {selected ? <IconFill/> : <IconEmpty/>}
                  </div>
                  {o.label}
                </li>
              );
            })
            : <li className={styles.listItem}>No options</li>
          }
        </ul>
        }
      </div>
    </div>
  );
}

export default SelectMulti;
